import {LatestOrderChangeRequest, OrderChangeBannerVariant} from '../types/orderChangeBanner.types'

/** Order statuses that indicate waiting for dispatcher confirmation */
const UNCONFIRMED_ORDER_STATUSES = ['pending', 'reserved']

export interface BannerState {
  showBanner: boolean
  variant: OrderChangeBannerVariant | null
}

const NO_BANNER: BannerState = {showBanner: false, variant: null}

const showBanner = (variant: OrderChangeBannerVariant): BannerState => ({showBanner: true, variant})

/**
 * Determines if the banner should be shown and what variant to display
 * based on the latest order change request status.
 */
export const determineBannerState = (
  request: LatestOrderChangeRequest | null | undefined,
  dismissedBanners: string[] = [],
  orderStatus?: string
): BannerState => {
  if (!request || dismissedBanners.includes(request.requestId)) {
    return NO_BANNER
  }

  const variant = resolveVariant(request, orderStatus)
  return variant ? showBanner(variant) : NO_BANNER
}

const resolveVariant = (
  request: LatestOrderChangeRequest,
  orderStatus?: string
): OrderChangeBannerVariant | null => {
  const {status, changesApplied, requestData} = request
  const requiresManualApproval = requestData?.requiresManualApproval ?? false

  // Request is still being sent to SAP
  if (status === 'sent') return 'changeRequestProcessing'

  // Request failed/rejected by SAP
  if (status === 'failed') return 'changeRequestFailed'

  if (status !== 'completed') return null

  // Completed but changes not yet applied — still processing
  if (!changesApplied) return 'changeRequestProcessing'

  // Manual approval: changes applied, waiting for dispatcher confirmation
  if (requiresManualApproval) {
    const isUnconfirmed =
      orderStatus && UNCONFIRMED_ORDER_STATUSES.includes(orderStatus.toLowerCase())
    return isUnconfirmed ? 'manualApprovalPending' : null
  }

  // Auto-approval: changes applied successfully, user can refresh to see new data
  return 'changesApplied'
}
